import React, { useState, useContext } from "react";
import CheckoutForm from "../CheckoutForm/CheckoutForm";
import {
  collection,
  getDocs,
  query,
  where,
  documentId,
  addDoc,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { CartContext } from "../../context/CartContext";
import { db } from "../../db/firebaseConfig";
import { Link } from "react-router-dom";

const Checkout = () => {
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [objOrder, setObjOrder] = useState(null);
  const [outOfStockItems, setOutOfStockItems] = useState([]);
  const { cart, clearCart } = useContext(CartContext);

  const createOrder = async ({ name, phone, email }) => {
    setLoading(true);
    setOutOfStockItems([]);

    try {
      const newOrder = {
        buyer: { name, phone, email },
        items: cart.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          subtotal: item.quantity * item.price,
        })),
        total: cart.reduce((acc, item) => acc + item.quantity * item.price, 0),
        date: Timestamp.fromDate(new Date()),
      };

      const batch = writeBatch(db);
      const outOfStock = [];
      const ids = cart.map((prod) => prod.id);
      const productsRef = collection(db, "producto");
      const productsFromFirestore = await getDocs(
        query(productsRef, where(documentId(), "in", ids))
      );

      productsFromFirestore.docs.forEach((doc) => {
        const dataDoc = doc.data();
        const stockDb = dataDoc.stock;
        const cartItem = cart.find((p) => p.id === doc.id);
        const qty = cartItem?.quantity;

        if (stockDb >= qty) {
          batch.update(doc.ref, { stock: stockDb - qty });
        } else {
          outOfStock.push({ id: doc.id, ...dataDoc });
        }
      });

      if (outOfStock.length === 0) {
        await batch.commit();
        const orderRef = await addDoc(collection(db, "orders"), newOrder);
        setOrderId(orderRef.id);
        clearCart();
        setObjOrder(newOrder);
      } else {
        setOutOfStockItems(outOfStock);
        console.error("Hay productos fuera de stock:", outOfStock);
      }
    } catch (error) {
      console.error("Error al crear la orden:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 mt-6 bg-white shadow-lg rounded-2xl">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-center text-gray-800">
        Pedido
      </h1>

      {loading && (
        <div className="text-center text-lg font-semibold text-gray-600">
          Generando la orden...
        </div>
      )}

      {/* Productos fuera de stock */}
      {outOfStockItems.length > 0 && (
        <div className="bg-red-100 border border-red-400 text-red-700 p-4 mb-6 rounded-lg">
          <h3 className="font-bold mb-2">Productos fuera de stock:</h3>
          <ul className="list-disc list-inside">
            {outOfStockItems.map((item) => (
              <li key={item.id}>
                {item.name} (Stock disponible: {item.stock})
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Orden generada */}
      {!loading && orderId && (
        <div className="bg-green-50 border border-green-300 p-6 rounded-lg text-gray-800">
          <h2 className="text-xl font-bold text-green-700 mb-2">
            ¡Gracias por tu pedido!
          </h2>
          <p className="text-sm mb-4">
            ID de la orden: <span className="font-mono">{orderId}</span>
          </p>

          <h3 className="mt-4 font-bold border-b border-green-300 pb-1">
            Detalle del pedido:
          </h3>
          <ul className="mt-2 space-y-1 text-sm">
            {objOrder.items.map((item) => (
              <li key={item.id}>
                {item.name} — Cantidad: {item.quantity} — Precio: $
                {item.price} — Subtotal: ${item.subtotal}
              </li>
            ))}
          </ul>

          <p className="font-bold text-lg mt-4">
            Total: ${objOrder.total.toLocaleString("es-AR")}
          </p>

          {/* ✅ Texto de agradecimiento y contacto */}
          <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200 text-sm leading-relaxed text-gray-700">
            <p>
              Gracias por tu pedido. Ahora debes dirigirte al negocio ubicado en{" "}
              <strong>Calle Los Inmigrantes 368, San José de la Dormida</strong>{" "}
              para realizar el pago y retiro del mismo.
            </p>
            <p className="mt-2">
              Si tienes dudas, comunicate al teléfono{" "}
              <a
                href="https://wa.me/5493512191101"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 font-semibold hover:underline"
              >
                351 219 1101 (WhatsApp)
              </a>
              .
            </p>
          </div>

          <div className="text-center mt-6">
            <Link
              to="/"
              className="inline-block bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg shadow-md transition"
            >
              Volver al inicio
            </Link>
          </div>
        </div>
      )}

      {/* Formulario de checkout */}
      {!loading && !orderId && (
        <div className="mt-4">
          <CheckoutForm onConfirm={createOrder} />
        </div>
      )}
    </div>
  );
};

export default Checkout;
